import React, { useEffect, useRef, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  CircularProgress,
  Box,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import AccessTimeIcon from "@mui/icons-material/AccessTime";

interface TranzilaPaymentModalProps {
  open: boolean;
  onClose: () => void;
  paymentUrl: string;
  reservationTimeoutMinutes?: number;
}

const TranzilaPaymentModal: React.FC<TranzilaPaymentModalProps> = ({
  open,
  onClose,
  paymentUrl,
  reservationTimeoutMinutes = 15,
}) => {
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const totalSeconds = reservationTimeoutMinutes * 60;
  const [timeLeft, setTimeLeft] = useState<number>(totalSeconds);
  const iframeRef = useRef<HTMLIFrameElement | null>(null);

  useEffect(() => {
    if (!open) return;
    setIsLoading(true);
    setTimeLeft(totalSeconds);

    const countdownInterval = setInterval(() => {
      setTimeLeft((prevTime) => {
        if (prevTime <= 1) {
          clearInterval(countdownInterval);
          onClose();
          return 0;
        }
        return prevTime - 1;
      });
    }, 1000);

    return () => clearInterval(countdownInterval);
  }, [open, totalSeconds, onClose]);

  const formatTime = (seconds: number) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${String(minutes).padStart(2, "0")}:${String(remainingSeconds).padStart(2, "0")}`;
  };

  useEffect(() => {
    if (iframeRef.current) {
      iframeRef.current.setAttribute("allowpaymentrequest", "true");
    }
  }, [open]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      slotProps={{
        paper: { sx: { borderRadius: 3, minHeight: "640px", overflow: "hidden" } },
      }}
    >
      <DialogTitle
        sx={{
          m: 0,
          p: 2,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          borderBottom: "1px solid",
          borderColor: "divider",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <LockOutlinedIcon color="primary" fontSize="small" />
          <Typography variant="h6" component="div" sx={{ fontWeight: 600 }}>
            תשלום מאובטח
          </Typography>
        </Box>

        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 0.8,
            backgroundColor: timeLeft < 120 ? "#ffebee" : "#f5f5f5",
            color: timeLeft < 120 ? "#d32f2f" : "#424242",
            px: 1.5,
            py: 0.5,
            borderRadius: 2,
            border: "1px solid",
            borderColor: timeLeft < 120 ? "#ef9a9a" : "#e0e0e0",
            transition: "all 0.3s ease",
          }}
        >
          <AccessTimeIcon fontSize="small" />
          <Typography variant="body2" sx={{ fontWeight: 600, direction: "ltr" }}>
            {formatTime(timeLeft)}
          </Typography>
          <Typography variant="caption" sx={{ display: { xs: "none", sm: "inline" } }}>
            שמירת מקום
          </Typography>
        </Box>

        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{ color: (theme) => theme.palette.grey[500] }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 0, position: "relative", height: "580px" }}>
        {isLoading && (
          <Box
            sx={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              gap: 2,
              backgroundColor: "background.paper",
              zIndex: 1,
            }}
          >
            <CircularProgress />
            <Typography variant="body2" color="text.secondary">
              טוען מסוף תשלום מאובטח...
            </Typography>
          </Box>
        )}

        <Box
          sx={{
            p: 1.5,
            backgroundColor: "#fff8e1",
            borderBottom: "1px solid #ffe082",
            textAlign: "center",
          }}
        >
          <Typography
            variant="body2"
            sx={{ color: "#d32f2f", fontWeight: "bold", fontSize: "13px" }}
          >
            שימו לב: כרגע לא ניתן לשלם באמצעות כרטיסי אמריקן אקספרס ודיינרס.
          </Typography>

          {/* Fallback Link for Strict Privacy Modes */}
          <Typography variant="caption" sx={{ display: "block", mt: 0.5 }}>
            המסך מוצג ריק או נתקע?{" "}
            <a
              href={paymentUrl}
              target="_blank"
              rel="noreferrer"
              style={{ color: "#1976d2", textDecoration: "underline", fontWeight: "bold" }}
            >
              לחץ כאן לפתיחת עמוד התשלום בחלון נפרד
            </a>
          </Typography>
        </Box>

        {paymentUrl && (
          <iframe
            id="tranzila-payment-frame"
            name="tranzila-payment-frame"
            title="Tranzila Secure Payment Frame"
            ref={iframeRef}
            src={paymentUrl}
            onLoad={() => setIsLoading(false)}
            allow="payment; fullscreen"
            style={{
              width: "100%",
              height: "calc(100% - 55px)",
              border: "none",
              display: "block",
            }}
          />
        )}
      </DialogContent>
    </Dialog>
  );
};

export default TranzilaPaymentModal;